import { useMemo } from "react";
import { toast } from "sonner";
import { Building2, CheckCircle2, Percent } from "lucide-react";
import { api } from "@/lib/api";
import { CardActions, Empty, Section } from "./redeShared";

export const ImobiliariasView = ({ leads, onOpen, onChanged }) => {
  const agencies = useMemo(
    () => leads.filter((l) => l.type === "agency" && l.status === "parceria_fechada"),
    [leads]
  );
  const blocked = useMemo(
    () => leads.filter((l) => l.type === "agency" && l.status === "bloqueado"),
    [leads]
  );

  const saveCashback = async (agency, raw) => {
    const pct = parseFloat(String(raw).replace(",", "."));
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error("Percentual inválido (0 a 100).");
      return;
    }
    try {
      await api.patch(`/admin/leads/${agency.id}`, { cashback_pct: pct });
      toast.success("Cashback da imobiliária atualizado.");
      onChanged();
    } catch {
      toast.error("Falha ao atualizar cashback.");
    }
  };

  return (
    <div data-testid="imobiliarias-view">
      <h1 className="font-display text-2xl font-bold text-petrol sm:text-3xl">
        Imobiliárias parceiras
      </h1>
      <p className="mt-1 text-sm text-slate-500">
        Imobiliárias e administradoras com parceria fechada com a Resolv.
      </p>

      <Section title="Rede ativa" sub={`${agencies.length} imobiliária(s) parceira(s).`} testId="imobiliarias-ativas">
        {agencies.length === 0 ? (
          <Empty>Nenhuma imobiliária parceira ainda. Avance os contatos na lista de Leads.</Empty>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {agencies.map((a) => (
              <div
                key={a.id}
                role="button"
                tabIndex={0}
                onClick={() => onOpen(a)}
                onKeyDown={(e) => e.key === "Enter" && onOpen(a)}
                data-testid={`rede-agency-card-${a.id}`}
                className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-5 text-left transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                    <Building2 className="h-5 w-5" />
                  </span>
                  <CardActions lead={a} onChanged={onChanged} />
                </div>
                <p className="mt-3 truncate font-display text-base font-bold text-petrol">
                  {a.payload?.company}
                </p>
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  {a.payload?.name} · {a.payload?.phone}
                </p>
                <p className="mt-2 truncate font-mono text-[0.65rem] uppercase tracking-wider text-slate-400">
                  {a.payload?.email}
                </p>
                <div
                  className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Percent className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <label className="flex items-center gap-1.5 text-[0.65rem] font-semibold text-slate-400">
                    Cashback
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      defaultValue={a.payload?.cashback_pct ?? 10}
                      key={`${a.id}-${a.payload?.cashback_pct ?? 10}`}
                      onBlur={(e) => {
                        const v = parseFloat(String(e.target.value).replace(",", "."));
                        if (!Number.isNaN(v) && v !== (a.payload?.cashback_pct ?? 10)) {
                          saveCashback(a, e.target.value);
                        }
                      }}
                      data-testid={`rede-agency-cashback-${a.id}`}
                      className="h-8 w-16 rounded-lg border border-slate-200 bg-white px-2 text-center text-xs text-petrol outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
                    />
                    % do lucro
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {blocked.length > 0 && (
        <Section
          title="Bloqueadas"
          sub="Imobiliárias bloqueadas na operação."
          testId="imobiliarias-bloqueadas"
        >
          <ul className="space-y-2">
            {blocked.map((b) => (
              <li
                key={b.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-red-100 bg-red-50/50 px-4 py-3"
                data-testid={`rede-blocked-${b.id}`}
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-petrol">
                    {b.payload?.company}
                  </span>
                  <span className="text-xs text-slate-500">{b.payload?.name}</span>
                </span>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    await api.patch(`/admin/leads/${b.id}`, { status: "novo" }).catch(() => {});
                    onChanged();
                  }}
                  data-testid={`rede-unblock-${b.id}`}
                  className="inline-flex shrink-0 items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Desbloquear
                </button>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};
